import { html, nothing, type TemplateResult } from "lit";

export type DockerLogsProps = {
  entries: string[];
  totalCount: number;
  filterText: string;
  autoFollow: boolean;
  connected: boolean;
  error: string | null;
  onFilterChange: (next: string) => void;
  onToggleAutoFollow: (next: boolean) => void;
  onClear: () => void;
  onReconnect: () => void;
  onScroll: (event: Event) => void;
  scrollAnchorRef: (el: HTMLElement | null) => void;
};

function dockerLogLevel(line: string): string | null {
  const lower = line.toLowerCase();
  if (lower.includes("error")) return "error";
  if (lower.includes("warn")) return "warn";
  if (lower.includes("info")) return "info";
  if (lower.includes("debug")) return "debug";
  return null;
}

function stripAnsi(text: string): string {
  return text.replace(/\x1b\[[0-9;]*m/g, "");
}

export function renderDockerLogs(props: DockerLogsProps): TemplateResult {
  return html`
    <section class="card logs-explorer">
      <div class="row" style="justify-content: space-between;">
        <div>
          <div class="card-title">Docker Logs</div>
          <div class="card-sub">
            Live container logs via <span class="mono">docker logs --follow</span>.
            ${
              props.connected
                ? html`
                    <span style="color: var(--green, #4caf50)">● Connected</span>
                  `
                : html`
                    <span style="color: var(--red, #f44336)">● Disconnected</span>
                  `
            }
          </div>
        </div>
        <div class="row" style="gap: 8px;">
          <button class="btn" @click=${props.onReconnect} ?disabled=${props.connected}>
            Reconnect
          </button>
          <button class="btn" @click=${props.onClear} ?disabled=${props.entries.length === 0}>
            Clear
          </button>
        </div>
      </div>

      <div class="muted" style="margin-top: 8px; font-size: 12px;">
        Showing ${props.entries.length} of ${props.totalCount} lines
      </div>

      <div class="filters" style="margin-top: 14px;">
        <label class="field" style="min-width: 220px;">
          <span>Filter</span>
          <input
            .value=${props.filterText}
            @input=${(e: Event) => props.onFilterChange((e.target as HTMLInputElement).value)}
            placeholder="Search docker logs"
          />
        </label>
        <label class="field checkbox">
          <span>Auto-follow</span>
          <input
            type="checkbox"
            .checked=${props.autoFollow}
            @change=${(e: Event) =>
              props.onToggleAutoFollow((e.target as HTMLInputElement).checked)}
          />
        </label>
      </div>

      ${props.error ? html`<div class="callout danger" style="margin-top: 10px;">${props.error}</div>` : nothing}

      <div
        class="log-stream logs-explorer__stream"
        style="margin-top: 12px;"
        @scroll=${props.onScroll}
      >
        ${
          props.entries.length === 0
            ? html`
              <div class="muted" style="padding: 12px">
                No docker log entries. ${
                  props.connected ? "Waiting for log output…" : "Click Reconnect to connect."
                }
              </div>
            `
            : props.entries.map(
                (line) => html`
                <div
                  class="log-row logs-explorer__row docker-log-row"
                  title=${line}
                >
                  <div class="log-time mono">${line.split(" ")[0] ?? ""}</div>
                  <div class="log-level ${dockerLogLevel(line) ?? ""}">
                    ${dockerLogLevel(line) ?? ""}
                  </div>
                  <div class="log-message mono docker-log-message">${stripAnsi(line)}</div>
                </div>
              `,
              )
        }
        <div ${props.scrollAnchorRef ? (el: Element | null) => props.scrollAnchorRef(el as HTMLElement | null) : nothing}></div>
      </div>
    </section>
  `;
}
